<template>
  <Dialog
    v-model:visible="visible"
    header="Receive from FSL"
    :modal="true"
    :style="{ width: '750px' }"
    @hide="resetForm"
  >
    <div class="fsl-receive-form">
      <!-- Pending FSL Batches -->
      <div v-if="fslBatches && fslBatches.length > 0" class="info-box">
        <h4>Pending at FSL:</h4>
        <div v-for="batch in fslBatches" :key="batch.fsl_id" class="fsl-batch">
          <Checkbox
            v-model="selectedBatches"
            :inputId="`batch-${batch.fsl_id}`"
            :value="batch.fsl_id"
          />
          <label :for="`batch-${batch.fsl_id}`" class="batch-label">
            <div class="batch-info">
              <strong>Sent: {{ formatDate(batch.date_sent) }}</strong>
              <span v-if="batch.acknowledgement_no" class="ack-no">
                (Ack: {{ batch.acknowledgement_no }})
              </span>
            </div>
            <div class="batch-quantity">
              Quantity Sent: {{ isWeightBased ? formatQuantity(batch.quantity_sent) : formatCount(batch.quantity_sent) }}
            </div>
          </label>
        </div>
      </div>

      <div v-else class="no-batches">
        <i class="ri-inbox-line"></i>
        <p>No pending batches at FSL</p>
      </div>

      <!-- Receive Details for Selected Batches -->
      <div v-for="batchId in selectedBatches" :key="batchId" class="receive-details">
        <div class="batch-header">
          <h4>{{ getBatchLabel(batchId) }}</h4>
        </div>

        <div class="form-group">
          <label>Date of Receipt: *</label>
          <Calendar
            v-model="receiveData[batchId].date_received"
            dateFormat="yy-mm-dd"
            class="w-full"
            :maxDate="new Date()"
          />
        </div>

        <div class="form-group">
          <label>Property Received Intact?</label>
          <div class="radio-group">
            <div class="radio-option">
              <RadioButton
                v-model="receiveData[batchId].received_intact"
                :inputId="`intact-${batchId}-yes`"
                :value="true"
              />
              <label :for="`intact-${batchId}-yes`">
                Yes - Full Quantity ({{ isWeightBased ? formatQuantity(getBatch(batchId).quantity_sent) : formatCount(getBatch(batchId).quantity_sent) }})
              </label>
            </div>
            <div class="radio-option">
              <RadioButton
                v-model="receiveData[batchId].received_intact"
                :inputId="`intact-${batchId}-no`"
                :value="false"
              />
              <label :for="`intact-${batchId}-no`">No - Partial/Damaged</label>
            </div>
          </div>
        </div>

        <!-- Quantity Received (if not intact) -->
        <div v-if="!receiveData[batchId].received_intact && isWeightBased" class="form-group">
          <label>Quantity Received: *</label>
          <InputNumber
            v-model="receiveData[batchId].quantity_received"
            :max="getBatch(batchId).quantity_sent"
            :min="0"
            :minFractionDigits="3"
            :maxFractionDigits="3"
            suffix=" g"
            class="w-full"
          />
          <small class="help-text">
            Sent: {{ formatQuantity(getBatch(batchId).quantity_sent) }}</small>
        </div>
        <div v-if="!receiveData[batchId].received_intact && !isWeightBased" class="form-group">
          <label>Quantity Received: *</label>
          <InputNumber
            v-model="receiveData[batchId].quantity_received"
            :max="getBatch(batchId).quantity_sent"
            :min="1"
            :minFractionDigits="0"
            :maxFractionDigits="0"
            suffix=" nos."
            class="w-full"
          />
          <small class="help-text">
            Sent: {{ formatCount(getBatch(batchId).quantity_sent) }}</small>
        </div>

        <!-- Discrepancy Remarks -->
        <div v-if="!receiveData[batchId].received_intact" class="form-group">
          <label>Discrepancy Remarks: *</label>
          <Textarea
            v-model="receiveData[batchId].discrepancy_remarks"
            rows="3"
            placeholder="Explain the discrepancy (partial return, damage, loss, consumed in testing, etc.)"
            class="w-full"
          />
        </div>
      </div>

      <!-- Overall Summary -->
      <div v-if="selectedBatches.length > 0" class="summary-box">
        <h4>Receiving Summary:</h4>
        <div class="summary-row">
          <span>Total Receiving:</span>
          <strong>{{ isWeightBased ? formatQuantity(totalReceiving) : formatCount(totalReceiving) }}</strong>
        </div>
        <div class="summary-row">
          <span>New PS Balance:</span>
          <strong>{{ isWeightBased ? formatQuantity(newPSBalance) : formatCount(newPSBalance) }}</strong>
        </div>
        <div class="summary-row">
          <span>Remaining at FSL:</span>
          <strong>{{ isWeightBased ? formatQuantity(remainingAtFSL) : formatCount(remainingAtFSL) }}</strong>
        </div>
      </div>
    </div>

    <template #footer>
      <Button
        label="Cancel"
        icon="ri-close-line"
        @click="visible = false"
        class="p-button-text"
      />
      <Button
        label="Mark as Received"
        icon="ri-checkbox-circle-line"
        @click="submitReceive"
        :loading="loading"
        :disabled="selectedBatches.length === 0"
      />
    </template>
  </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import RadioButton from 'primevue/radiobutton';
import InputNumber from 'primevue/inputnumber';
import Calendar from 'primevue/calendar';
import Textarea from 'primevue/textarea';

export default {
  name: 'ReceiveFromFSLDialog',
  components: {
    Dialog,
    Button,
    Checkbox,
    RadioButton,
    InputNumber,
    Calendar,
    Textarea
  },
  props: {
    property: {
      type: Object,
      required: true
    },
    fslBatches: {
      type: Array,
      default: () => []
    },
    currentPSQuantity: {
      type: Number,
      default: 0
    },
    currentFSLQuantity: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      visible: false,
      loading: false,
      selectedBatches: [],
      receiveData: {}
    };
  },
  computed: {
    isWeightBased() {
      const name = (this.property?.property_type?.property_type || this.property?.PropertyType?.property_type || '').toString().toLowerCase();
      const keywords = ['drug', 'gold', 'silver', 'diamond', 'precious metal', 'narcotic', 'grams', 'contraband'];
      return keywords.some(kw => name.includes(kw));
    },
    totalReceiving() {
      return this.selectedBatches.reduce((sum, batchId) => {
        const data = this.receiveData[batchId];
        const batch = this.getBatch(batchId);
        if (!data || !batch.fsl_id) return sum;
        return sum + (data.received_intact ? batch.quantity_sent : (data.quantity_received || 0));
      }, 0);
    },
    newPSBalance() {
      return this.currentPSQuantity + this.totalReceiving;
    },
    remainingAtFSL() {
      return this.currentFSLQuantity - this.totalReceiving;
    }
  },
  methods: {
    open() {
      this.visible = true;
      this.initializeReceiveData();
    },
    resetForm() {
      this.selectedBatches = [];
      this.receiveData = {};
    },
    initializeReceiveData() {
      this.selectedBatches = [];
      this.receiveData = {};
      if (this.fslBatches) {
        this.fslBatches.forEach(batch => {
          this.receiveData[batch.fsl_id] = {
            date_received: new Date(),
            received_intact: true,
            quantity_received: batch.quantity_sent,
            discrepancy_remarks: ''
          };
        });
      }
    },
    getBatch(batchId) {
      return this.fslBatches.find(b => b.fsl_id === batchId) || {};
    },
    getBatchLabel(batchId) {
      const batch = this.getBatch(batchId);
      return `Batch sent on ${this.formatDate(batch.date_sent)}`;
    },
    formatQuantity(qty) {
      const n = Number(qty);
      if (qty == null || qty === '' || isNaN(n)) return '0 g';
      if (n >= 1000) {
        return `${(n / 1000).toFixed(3)} kg`;
      }
      return `${n.toFixed(3)} g`;
    },
    formatCount(qty) {
      const n = Number(qty);
      if (qty == null || qty === '' || isNaN(n)) return '0 nos.';
      return `${Math.floor(n)} nos.`;
    },
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    formatDateForAPI(date) {
      if (!date) return '';
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    async submitReceive() {
      for (const batchId of this.selectedBatches) {
        const data = this.receiveData[batchId];
        const batch = this.getBatch(batchId);

        if (!data.date_received) {
          this.$store.dispatch('error', 'Please select receipt date for all batches');
          return;
        }

        if (!data.received_intact) {
          if (!data.quantity_received || data.quantity_received <= 0) {
            this.$store.dispatch('error', 'Please enter valid quantity received');
            return;
          }
          if (data.quantity_received > batch.quantity_sent) {
            this.$store.dispatch('error', 'Received quantity cannot exceed sent quantity');
            return;
          }
          if (!data.discrepancy_remarks || !data.discrepancy_remarks.trim()) {
            this.$store.dispatch('error', 'Please provide discrepancy remarks');
            return;
          }
        }
      }

      this.loading = true;
      try {
        await this.$store.dispatch('post', {
          uri: 'receivePropertyFromFSL',
          data: {
            property_id: this.property.property_id,
            batches: this.selectedBatches.map(batchId => ({
              fsl_id: batchId,
              date_received: this.formatDateForAPI(this.receiveData[batchId].date_received),
              received_intact: this.receiveData[batchId].received_intact,
              quantity_received: this.receiveData[batchId].received_intact
                ? this.getBatch(batchId).quantity_sent
                : this.receiveData[batchId].quantity_received,
              discrepancy_remarks: this.receiveData[batchId].discrepancy_remarks
            }))
          }
        });

        this.$store.dispatch('success', 'Property received from FSL successfully');
        this.$emit('refresh');
        this.visible = false;
      } catch (error) {
        this.$store.dispatch('error', error.response?.data?.message || 'Failed to receive property from FSL');
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>
.fsl-receive-form {
  padding: 10px 0;
}

.info-box {
  background: #FFF3E0;
  border: 1px solid #FF9800;
  border-radius: 6px;
  padding: 15px;
  margin-bottom: 20px;
}

.info-box h4 {
  margin: 0 0 15px 0;
  font-size: 14px;
  color: #E65100;
}

.fsl-batch {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: white;
  border-radius: 4px;
  margin-bottom: 10px;
  border: 1px solid #FFE0B2;
}

.fsl-batch:last-child {
  margin-bottom: 0;
}

.batch-label {
  flex: 1;
  cursor: pointer;
}

.batch-info {
  margin-bottom: 5px;
}

.batch-info strong {
  color: #333;
}

.ack-no {
  color: #666;
  font-size: 13px;
  margin-left: 8px;
}

.batch-quantity {
  color: #555;
  font-size: 14px;
}

.no-batches {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}

.no-batches i {
  font-size: 48px;
  margin-bottom: 10px;
  display: block;
}

.receive-details {
  background: #F9F9F9;
  border-radius: 6px;
  padding: 15px;
  margin-bottom: 15px;
}

.batch-header h4 {
  margin: 0 0 15px 0;
  color: #333;
  font-size: 14px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e0e0e0;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

.radio-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.radio-option {
  display: flex;
  align-items: center;
  gap: 10px;
}

.radio-option label {
  margin: 0;
  cursor: pointer;
}

.help-text {
  display: block;
  margin-top: 5px;
  color: #666;
  font-size: 12px;
}

.summary-box {
  background: #E8F5E9;
  border: 1px solid #4CAF50;
  border-radius: 6px;
  padding: 15px;
  margin-top: 20px;
}

.summary-box h4 {
  margin: 0 0 10px 0;
  font-size: 14px;
  color: #2E7D32;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #C8E6C9;
}

.summary-row:last-child {
  border-bottom: none;
}

.w-full {
  width: 100%;
}
</style>
